import React, { useEffect, useRef } from "react"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useScrollReveal, trackStyles, deriveTimeline } from "../utils"

gsap.registerPlugin(ScrollTrigger)

function HorizontalTimeline({ programme, styleMap, hoveredTrack }) {
  const timeline = deriveTimeline(programme.sessions, programme.tracks, styleMap)
  const hasPreSessions = timeline.some(t => t.month === "0")
  const monthNumbers = Array.from({ length: programme.duration_months }, (_, i) => i + 1)
  const columns = [
    ...(hasPreSessions ? [{ label: "Pre", key: "0" }] : []),
    ...monthNumbers.map(m => ({ label: `Mo. ${m}`, key: String(m) })),
  ]
  const byColumn = columns.map(col => ({
    label: col.label,
    key: col.key,
    events: timeline.filter(t => t.month === col.key),
  }))

  return (
    <div style={{ overflowX: "auto", paddingBottom: 8 }}>
      <div style={{ minWidth: 700, position: "relative", paddingTop: 8 }}>

        {/* Connecting line */}
        <div style={{
          position: "absolute",
          top: 28,
          left: `calc(100% / ${columns.length * 2})`,
          right: `calc(100% / ${columns.length * 2})`,
          height: 1,
          background: "rgba(255,255,255,0.15)",
        }} />

        <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}>
          {byColumn.map(({ label, key, events }) => (
            <div key={key} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 24, position: "relative", zIndex: 1 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#fff", marginBottom: 10 }}>
                  {label}
                </span>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fff" }} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", padding: "0 8px", boxSizing: "border-box" }}>
                {events.map((ev, i) => {
                  const ts = trackStyles[ev.trackStyle]
                  const dimmed = hoveredTrack && ev.trackStyle !== hoveredTrack
                  return (
                    <div key={i} style={{
                      borderLeft: `2px ${ts.borderStyle} ${ts.color}`,
                      paddingLeft: 10, paddingTop: 6, paddingBottom: 6,
                      opacity: dimmed ? 0.15 : 1,
                      transition: "opacity 0.2s ease",
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: ts.color, display: "block", marginBottom: 4 }}>
                        {ev.track}
                      </span>
                      <span style={{ fontSize: 12, lineHeight: 1.45, color: "#fff", display: "block" }}>
                        {ev.session}
                      </span>
                      {ev.detail && (
                        <span style={{ fontSize: 11, color: "#fff", display: "block", marginTop: 3 }}>{ev.detail}</span>
                      )}
                      <span style={{ fontSize: 11, color: "#fff", display: "block", marginTop: 3 }}>{ev.format}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  )
}

export function TimelineBlock({ programme, styleMap, hoveredTrack, setHoveredTrack }) {
  const headerRef = useScrollReveal()
  const rowRefs = useRef([])
  const timeline = deriveTimeline(programme.sessions, programme.tracks, styleMap)
  const oh = programme.office_hours

  useEffect(() => {
    const ctx = gsap.context(() => {
      rowRefs.current.forEach(el => {
        if (!el) return
        gsap.fromTo(el,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.6, ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 92%" } }
        )
      })
    })
    return () => ctx.revert()
  }, [])

  return (
    <div ref={headerRef} style={{ padding: "40px 60px 0", boxSizing: "border-box" }}>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.12)", paddingTop: 60 }}>
        <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", color: "#fff", margin: "0 0 48px" }}>
          Programme timeline
        </p>

        <div style={{ marginBottom: 64 }}>
          <HorizontalTimeline programme={programme} styleMap={styleMap} hoveredTrack={hoveredTrack} />
        </div>

        {/* Table header */}
        <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 100px 140px", gap: "0 40px", paddingBottom: 12, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          {["Mo.", "Session", "Track", "Format"].map(h => (
            <span key={h} style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#fff" }}>{h}</span>
          ))}
        </div>

        {timeline.map((row, i) => {
          const ts = trackStyles[row.trackStyle]
          const dimmed = hoveredTrack && row.trackStyle !== hoveredTrack
          return (
            <div
              key={i}
              ref={el => { rowRefs.current[i] = el }}
              style={{ display: "grid", gridTemplateColumns: "60px 1fr 100px 140px", gap: "0 40px", padding: "22px 0", borderBottom: "1px solid rgba(255,255,255,0.07)", alignItems: "start", opacity: dimmed ? 0.15 : 1, transition: "opacity 0.2s ease" }}
            >
              <span style={{ fontSize: 13, color: "#fff", fontVariantNumeric: "tabular-nums" }}>{row.month === "0" ? "Pre" : row.month}</span>
              <div style={{ borderLeft: `2px ${ts.borderStyle} ${ts.color}`, paddingLeft: 14 }}>
                <span style={{ fontSize: 15, fontWeight: 400, color: "#fff", lineHeight: 1.4, display: "block" }}>{row.session}</span>
                {row.detail && <span style={{ fontSize: 12, color: "#fff", display: "block", marginTop: 2 }}>{row.detail}</span>}
                <span style={{ fontSize: 13, color: "#fff", display: "block", marginTop: 6, lineHeight: 1.5 }}>{row.purpose}</span>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: ts.color, paddingTop: 2 }}>{row.track}</span>
              <span style={{ fontSize: 13, color: "#fff", paddingTop: 2 }}>{row.format}</span>
            </div>
          )
        })}

        {oh && (
          <p style={{ fontSize: 13, color: "#fff", marginTop: 28, lineHeight: 1.6 }}>
            <strong style={{ fontWeight: 600 }}>{oh.cadence} office hours</strong>{oh.optional ? " (optional)" : ""} run throughout the full {programme.duration_months} months ({oh.duration}, open to {oh.open_to.toLowerCase()}) for {oh.purpose.toLowerCase()}.
          </p>
        )}
      </div>
    </div>
  )
}
